import PublicLayout from "@/components/PublicLayout";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles,
  HeartHandshake,
  Sun,
  TrendingUp,
  Feather,
  Flame,
  CheckCircle2,
  BookOpen,
  ArrowRight,
  Download,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

export default function Dashboard() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [verse, setVerse] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/analytics/overview").then((r) => setOverview(r.data)),
      api.get("/bible/daily-verse").then((r) => setVerse(r.data)),
    ]).catch(() => {});
  }, []);

  const downloadExport = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/me/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `path-to-repentance-export-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Your spiritual report is saved.");
    } catch (e) {
      toast.error("Could not prepare your export. Try again.");
    }
    setExporting(false);
  };

  const greet = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  })();

  return (
    <PublicLayout>
      <section className="bg-brand-base">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-brand-quiet">Your sanctuary</p>
              <h1 className="mt-2 font-serif text-4xl sm:text-5xl text-brand-ink tracking-tight">
                {greet}, {user?.anonymous_mode ? "friend" : user?.name?.split(" ")[0] || "friend"}.
              </h1>
              <p className="mt-1 text-brand-quiet">May today bring you closer to His grace.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link
                to="/chat"
                data-testid="dashboard-chat-link"
                className="rounded-full bg-brand-sky text-white hover:bg-brand-skyDark transition-colors px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4" strokeWidth={1.7} /> Talk to Grace
              </Link>
              <button
                onClick={downloadExport}
                disabled={exporting}
                data-testid="dashboard-export-button"
                className="rounded-full bg-white border border-brand-line text-brand-ink hover:bg-brand-mist transition-colors px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60"
              >
                <Download className="w-4 h-4" strokeWidth={1.7} />
                {exporting ? "Preparing…" : "Download my report"}
              </button>
            </div>
          </div>

          {/* Bento grid */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
            <StatCard
              testid="stat-streak"
              icon={Flame}
              label="Days of grace"
              value={overview?.streak_days ?? "—"}
              hint="since your last recorded fall"
              color="text-brand-gold"
            />
            <StatCard
              testid="stat-growth"
              icon={TrendingUp}
              label="Spiritual growth"
              value={overview?.growth_score != null ? `${overview.growth_score}` : "—"}
              hint="out of 100"
              color="text-brand-sage"
            />
            <StatCard
              testid="stat-prayers"
              icon={HeartHandshake}
              label="Prayers offered"
              value={overview?.prayer_count ?? "—"}
              hint={`${overview?.answered_prayers ?? 0} answered`}
              color="text-brand-skyDark"
            />

            <div className="md:col-span-2 rounded-2xl bg-white border border-brand-line p-7">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-serif text-2xl text-brand-ink">Last 8 weeks</h3>
                <Link
                  to="/analytics"
                  data-testid="dashboard-analytics-link"
                  className="text-sm text-brand-skyDark hover:underline inline-flex items-center gap-1"
                >
                  See deeper <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={overview?.weekly || []}
                    margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="failGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#D9A098" stopOpacity={0.45} />
                        <stop offset="100%" stopColor="#D9A098" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="resistGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#8EAC9A" stopOpacity={0.45} />
                        <stop offset="100%" stopColor="#8EAC9A" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E9EB" />
                    <XAxis dataKey="week" stroke="#94A3B8" fontSize={11} />
                    <YAxis stroke="#94A3B8" fontSize={11} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{
                        background: "#fff",
                        border: "1px solid #E5E9EB",
                        borderRadius: 12,
                        fontSize: 12,
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="failures"
                      stroke="#D9A098"
                      strokeWidth={2}
                      fill="url(#failGrad)"
                      name="Falls"
                    />
                    <Area
                      type="monotone"
                      dataKey="resisted"
                      stroke="#8EAC9A"
                      strokeWidth={2}
                      fill="url(#resistGrad)"
                      name="Resisted"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="rounded-2xl sun-rays text-white p-7 relative overflow-hidden">
              <div className="absolute -right-8 -top-8 w-32 h-32 bg-white/15 rounded-full blur-2xl" />
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/80">
                <Sun className="w-4 h-4" strokeWidth={1.7} /> Verse for today
              </div>
              <p className="mt-4 scripture-quote text-2xl leading-snug text-balance">
                “{verse?.text}”
              </p>
              <p className="text-xs text-white/75 mt-3">— {verse?.reference}</p>
            </div>

            <div className="md:col-span-2 rounded-2xl bg-white border border-brand-line p-7">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-serif text-2xl text-brand-ink">Recurring struggles</h3>
                <Link
                  to="/sin-tracker"
                  data-testid="dashboard-sintracker-link"
                  className="text-sm text-brand-skyDark hover:underline inline-flex items-center gap-1"
                >
                  Log a moment <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
              {(!overview?.top_sins || overview.top_sins.length === 0) ? (
                <p className="text-sm text-brand-quiet">
                  No recorded struggles yet. When you are ready, your sin tracker is a safe place to be honest.
                </p>
              ) : (
                <ul className="space-y-3">
                  {overview.top_sins.map((s) => (
                    <li key={s.name} className="flex items-center gap-4">
                      <span className="text-sm text-brand-ink min-w-[7rem]">{s.name}</span>
                      <div className="flex-1 h-2 rounded-full bg-brand-mist overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-brand-setback to-brand-sky"
                          style={{
                            width: `${Math.min(
                              100,
                              (s.count /
                                Math.max(...overview.top_sins.map((x) => x.count))) *
                                100
                            )}%`,
                          }}
                        />
                      </div>
                      <span className="text-sm text-brand-quiet w-8 text-right">{s.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-2xl bg-white border border-brand-line p-7">
              <h3 className="font-serif text-2xl text-brand-ink mb-3">Next steps</h3>
              <ul className="space-y-3 text-sm">
                <Step icon={Sparkles} text="Open the AI Companion when you feel tempted." to="/chat" testid="next-chat" />
                <Step icon={Feather} text="Log honestly — both the falls and the resists." to="/sin-tracker" testid="next-tracker" />
                <Step icon={HeartHandshake} text="Write one prayer today." to="/prayer" testid="next-prayer" />
                <Step icon={BookOpen} text="Read a teaching of Jesus." to="/bible" testid="next-bible" />
              </ul>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

function StatCard({ icon: Icon, label, value, hint, color, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-brand-line p-6" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-brand-quiet">{label}</span>
        <Icon className={`w-5 h-5 ${color}`} strokeWidth={1.6} />
      </div>
      <div className="mt-4 font-serif text-5xl text-brand-ink leading-none">{value}</div>
      <p className="text-xs text-brand-muted mt-2">{hint}</p>
    </div>
  );
}

function Step({ icon: Icon, text, to, testid }) {
  return (
    <li>
      <Link
        to={to}
        data-testid={testid}
        className="flex items-center gap-3 text-brand-ink hover:text-brand-skyDark group"
      >
        <span className="w-8 h-8 rounded-full bg-brand-mist text-brand-skyDark grid place-items-center group-hover:bg-brand-sky group-hover:text-white transition-colors">
          <Icon className="w-4 h-4" strokeWidth={1.6} />
        </span>
        <span className="flex-1">{text}</span>
        <CheckCircle2 className="w-4 h-4 text-brand-line group-hover:text-brand-sage" strokeWidth={1.5} />
      </Link>
    </li>
  );
}
